import type { FC } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import type { KeyedMutator } from "swr";

import type { XStepData } from "../hooks";
import { useXStep } from "../hooks";

export type XStepState = XStepData;

export type XStepUpdater = KeyedMutator<XStepData>;

const XStepContextState = createContext<XStepState>({
  apr: 0,
  stepPerXStep: 0,
  tvl: 0,
  price: 0,
});

const XStepContextRefresher = createContext<XStepUpdater>(Promise.resolve);

export const XStepProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const {
    xStepData: { apr, price, stepPerXStep, tvl },
    refresh,
  } = useXStep();

  const memoizedXStepData = useMemo(
    () => ({ apr, price, stepPerXStep, tvl } as XStepData),
    [apr, price, stepPerXStep, tvl],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedRefresh = useCallback(refresh, []);

  return (
    <XStepContextState.Provider value={memoizedXStepData}>
      <XStepContextRefresher.Provider value={memoizedRefresh}>{children}</XStepContextRefresher.Provider>
    </XStepContextState.Provider>
  );
};

export const useXStepContext = () => {
  const context = useContext(XStepContextState);
  return context;
};

export const useXStepContextRefresher = () => {
  const context = useContext(XStepContextRefresher);
  return context;
};
